import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Button, Modal, Form } from "react-bootstrap";
import Select from 'react-select';
import { toast } from "react-toastify";
import api from "../../services/api";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllRoles } from "../../redux/slice/getRolesPermissionListSlice";
import { fetchAllPermissions } from "../../redux/slice/getAllPermissionListSlice";
import CrossIcon from "../../assets/img/file-cut-icon.png";
import axiosPrivate from "../../services/axios/axiosPrivate";
import { useForm } from "react-hook-form";

function RoleModals({ show, onHide, roleId, roleName, description, canEdit, existingPermissions }) {
  const dispatch = useDispatch();
  const { permissions, status } = useSelector((state) => state.Permissions);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  
  const { 
    register, 
    handleSubmit, 
    formState: { errors }, 
  } = useForm({
    mode: "onChange",
    defaultValues: {
      roleName: roleName || "",
      description: description || ""
    }
  });

  useEffect(() => {
    if (show) {
      dispatch(fetchAllPermissions());
    }
  }, [show, dispatch]);

  useEffect(() => {
    if (existingPermissions && permissions.length > 0) {
      const selected = existingPermissions.map(perm => ({
        value: perm.id,
        label: perm.name,
        description: perm.description
      }));
      setSelectedPermissions(selected);
    }
  }, [existingPermissions, permissions]);

  const permissionOptions = permissions.map(permission => ({
    value: permission.id,
    label: permission.name,
    description: permission.description
  }));

  const handleUpdateRole = async (data) => {
    if (!canEdit) {
      toast.error("You do not have permission to edit the role.", { className: 'toast-error-custom', autoClose: 5000 });
      return;
    }
    try {
      const payload = {
        name: data.roleName,
        description: data.description,
        permissionIds: selectedPermissions.map(p => p.value)
      };

      const response = await axiosPrivate.put(
        api.roles.update(roleId),
        payload
      );
      if (response?.data?.success === true) {
        toast.success(response?.data?.reason || "Role updated successfully!", { className: 'toast-success-custom' });
        await dispatch(fetchAllRoles()).unwrap();
        onHide();
      } else {
        toast.error(response?.data?.reason || "Failed to update role", { className: 'toast-error-custom', autoClose: 8000 });
      }
    } catch {
      toast.error("Unable to connect to server");
    }
  };
  
  const onUpdate = (data) => {
    handleUpdateRole(data); 
  };

  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '38px',
      borderColor: '#ced4da',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#e7f3ff',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#0066cc',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#0066cc',
      ':hover': {
        backgroundColor: '#0066cc',
        color: 'white',
      },
    }),
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header>
        <Modal.Title>Edit Role</Modal.Title>
        <button onClick={onHide} className="modal-close">
          <img src={CrossIcon} alt="Close" style={{ width: "20px", height: "20px" }} />
        </button>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onUpdate)}>
          <Form.Group>
            <Form.Label>Role Name</Form.Label>
            <Form.Control
            type="text"
            {...register("roleName", {
              required: "Role name is required",
              minLength: { value: 2, message: "Name must be at least 2 characters" },
              maxLength: { value: 50, message: "Name cannot exceed 50 characters" },
               pattern: {
                value: /^[a-zA-Z\s]+$/, 
                message: "Numbers and special characters are not allowed"
               }
            })}
            placeholder="Role Name"
            />
            {errors.roleName && (
            <p className="error-msg">
              {errors.roleName.message}
            </p>
          )}
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
            as="textarea"
            {...register("description", {
              required: "Description is required",
              maxLength: { value: 200, message: "Keep description under 200 characters" },
               pattern: {
                value: /^[a-zA-Z\s]+$/, 
                message: "Numbers and special characters are not allowed"
               }
            })}
            placeholder="Role Description"
            />
            {errors.description && (
            <p className="error-msg">
              {errors.description.message}
            </p>
          )}
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Default Permissions</Form.Label>
            <Select
              isMulti
              options={permissionOptions}
              value={selectedPermissions}
              onChange={setSelectedPermissions}
              placeholder="Search and select permissions..."
              styles={customStyles}
              isLoading={status === 'loading'}
              isDisabled={status === 'loading'}
              className="basic-multi-select"
              classNamePrefix="select"
              formatOptionLabel={(option) => (
                <div>
                  <div style={{ fontWeight: '500' }}>{option.label}</div>
                  {option.description && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {option.description}
                    </div>
                  )}
                </div>
              )}
            />
            <Form.Text className="text-muted">
              Update default permissions for this role.
            </Form.Text>
            {selectedPermissions.length > 0 && (
              <div className="mt-2">
                <small className="text-info">
                  {selectedPermissions.length} permission(s) selected
                </small>
              </div>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="cancel-cta" onClick={onHide}>Cancel</Button>
        <Button variant="update-cta" onClick={handleSubmit(onUpdate)} disabled={!canEdit}>Update</Button>
      </Modal.Footer>
    </Modal>
  );
}

RoleModals.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  roleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  roleName: PropTypes.string,
  description: PropTypes.string,
  canEdit: PropTypes.bool,
  existingPermissions: PropTypes.array
};

export default RoleModals;
